import React, { CSSProperties, useEffect, useState } from "react";

import { Alert, Col, Radio, Row, Tabs } from "antd";
import {
  formatFileSize,
  lightenDarkenColor,
  useCSVReader,
} from "react-papaparse";

import { DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  excelFormatTypes,
  handleJazzZongExcelDataImportAndFormulateTheData,
  handleNBBDataImportAndFormulateTheData,
  handleNetwalaExcelDataImportAndFormulateTheData,
  handleRedToneExcelDataImportAndFormulateTheData,
  handleTransworldDataImportAndFormulateTheData,
  handleZalProDataImportAndFormulateTheData,
  importFormatIspTypes,
} from "../../../../../helpers/bulkUsers";
import { ButtonProps } from "../../../../../helpers/constants";
import useAxiosPost, {
  authTokenString,
} from "../../../../../hooks/useAxiosPost";
import useMultiNotification from "../../../../../hooks/useNotification";
import { socket } from "../../../../../socketService";
import NetwalaButton from "../../../../Items/Button/Index";
import TailSpinLoader from "../../../../Items/Loaders/TailSpinLoader";
import NetwalaSelect from "../../../../Items/Select/Index";
import BulkUsersImportTable from "../../../../Items/Table/BulkUsersImportDataTable";
import PackageMapperTable from "../../../../Items/Table/PackageMapperTable";
import CreatePackageOnTheGoModalComponent from "../../../../Modals/CreatePackageOnTheGoModal";
import ImportUserConfirmationModal from "../../../../Modals/ImportUserConfirmationModal";
import BackButtonLong from "./../../../../../assets/svgs/BackButtonLong.svg";
import PlusIcon from "./../../../../../assets/svgs/IconsWhite/PlusIcon.svg";
import "./index.css";

const GREY = "#CCC";
const GREY_LIGHT = "rgba(255, 255, 255, 0.4)";
const DEFAULT_REMOVE_HOVER_COLOR = "#A01919";
const REMOVE_HOVER_COLOR_LIGHT = lightenDarkenColor(
  DEFAULT_REMOVE_HOVER_COLOR,
  40
);
const GREY_DIM = "#686868";

const styles = {
  zone: {
    cursor: "pointer",
    alignItems: "center",
    border: `2px dashed ${GREY}`,
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    padding: 20,
  } as CSSProperties,
  file: {
    background: "linear-gradient(to bottom, #EEE, #DDD)",
    borderRadius: 20,
    display: "flex",
    height: 120,
    width: 120,
    position: "relative",
    zIndex: 10,
    flexDirection: "column",
    justifyContent: "center",
  } as CSSProperties,
  info: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    paddingLeft: 10,
    paddingRight: 10,
  } as CSSProperties,
  size: {
    backgroundColor: GREY_LIGHT,
    borderRadius: 3,
    marginBottom: "0.5em",
    justifyContent: "center",
    display: "flex",
  } as CSSProperties,
  name: {
    backgroundColor: GREY_LIGHT,
    borderRadius: 3,
    fontSize: 12,
    marginBottom: "0.5em",
  } as CSSProperties,
  progressBar: {
    bottom: 14,
    position: "absolute",
    width: "100%",
    paddingLeft: 10,
    paddingRight: 10,
  } as CSSProperties,
  zoneHover: {
    borderColor: GREY_DIM,
  } as CSSProperties,
  default: {
    borderColor: GREY,
  } as CSSProperties,
  remove: {
    height: 23,
    position: "absolute",
    right: 6,
    top: 6,
    width: 23,
  } as CSSProperties,
};

const ISPBulkUserImport: React.FC<SingleISPProfileTabProp> = ({
  intPkIspID,
}) => {
  const { openNotification, contextHolder } = useMultiNotification();
  socket;
  const { CSVReader } = useCSVReader();
  const [zoneHover, setZoneHover] = useState(false);
  const [removeHoverColor, setRemoveHoverColor] = useState(
    DEFAULT_REMOVE_HOVER_COLOR
  );
  const [expiryDateError, setExpiryDateError] = useState<string | null>(null);
  const [expiryIssuesNotice, setExpiryIssuesNotice] = useState<string | null>(
    null,
  );
  const [fileType, setFileType] = useState<"CSV" | "EXCEL">("CSV");
  const [selectedFormat, setSelectedFormat] = useState<string>("");
  const [selectedExcelFormat, setSelectedExcelFormat] = useState<string>("");
  const [isSelectorDisabled, setIsSelectorDisabled] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [ispPackages, setIspPackages] = useState<string[]>([]);
  const [users, setUsers] = useState<BulkUserImportData[]>([]);
  const [chunks, setChunks] = useState<BulkUserImportData[][]>([]);
  const [totalChunks, setTotalChunks] = useState<number>(0);
  const [chunkSize, setChunkSize] = useState<number>(250);
  const [show, setShow] = useState<boolean>(false);
  const [showCreatePackage, setShowCreatePackage] = useState<boolean>(false);
  const [usersWithNoPackage, setUsersWithNoPackage] = useState<
    BulkUserImportData[]
  >([]);
  const [activeStep, setActiveStep] = useState<number>(1);
  const [duplicateSummary, setDuplicateSummary] = useState<
    { number: string; count: number }[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPackagesMapped, setIsPackagesMapped] = useState<boolean>(false);
  const navigate = useNavigate();
  const [packages, setPackages] = useState<PackageWithAsset[]>([]);
  const [mappedPackages, setMappedPackages] = useState<MappedPackagesProp[]>(
    []
  );
  const [activeTab, setActiveTab] = useState<string>("users");
  const [
    postApiData,
    apiResponse,
    apiError,
    apiLoading,
    apiMessage,
    apiErrorCount,
  ] = useAxiosPost<PackagesAndVlansByISPApiProp>("packages/getPackagesByISP");
  const [
    createNewBulkRequestApi,
    bulkRequestApiResponse,
    bulkRequestApiError,
    bulkRequestApiLoading,
    bulkRequestApiMessage,
    bulkRequestApiErrorCount,
  ] = useAxiosPost<CreateNewBulkRequestApiProp>("isps/createBulkImportRequest");

  const [
    bulkImportUsersByIspApi,
    usersImportRequestApiResponse,
    usersImportRequestApiError,
    usersImportRequestApiLoading,
    usersImportRequestApiMessage,
    usersImportRequestApiErrorCount,
  ] = useAxiosPost<BulkImportUsersApiRequest>("isps/bulkImportUser");
  useEffect(() => {
    if (apiResponse !== null) {
      const data: any = apiResponse.responseData;
      setPackages(data);
    }
  }, [apiResponse]);

  useEffect(() => {
    postApiData({ ispId: intPkIspID });
    socket.emit(
      "join",
      {
        userName: localStorage.getItem("username") || "client",
        client: "web",
      },
      () => {}
    );
  }, []);
  const handleSelectedFormat = (value: string) => {
    setSelectedFormat(value);
    setDuplicateSummary([]);
    setExpiryIssuesNotice(null);
  };
  const checkForDuplicatePhoneNumbers = (
    usersToCheck: BulkUserImportData[],
  ): string[] => {
    const phoneNumberMap = new Map<string, number>();
    const duplicates: string[] = [];

    usersToCheck.forEach((user) => {
      const phoneNumber = user.phoneNumber?.trim() || "";
      if (phoneNumber) {
        const count = phoneNumberMap.get(phoneNumber) || 0;
        phoneNumberMap.set(phoneNumber, count + 1);
        if (count === 1) {
          // First duplicate found, add to duplicates array
          duplicates.push(phoneNumber);
        }
      }
    });

    return duplicates;
  };
  //duplicate number counter
  const getDuplicateSummary = (
    users: BulkUserImportData[],
  ): { number: string; count: number }[] => {
    const map = new Map<string, number>();

    users.forEach((user) => {
      const phone = (user.phoneNumber || "")
        .toString()
        .replace(/\s+/g, "")
        .trim();

      if (!phone) return;

      map.set(phone, (map.get(phone) || 0) + 1);
    });

    const duplicates: { number: string; count: number }[] = [];

    map.forEach((count, number) => {
      if (count > 1) {
        duplicates.push({ number, count });
      }
    });

    return duplicates;
  };

  //add
  const normalizeDuplicatePhoneNumbers = (
    users: BulkUserImportData[],
  ): BulkUserImportData[] => {
    const phoneCounter = new Map<string, number>();

    return users.map((user) => {
      const originalPhone = (user.phoneNumber || "")
        .toString()
        .replace(/\s+/g, "") // remove ALL spaces
        .trim();

      if (!originalPhone) return user;

      const count = phoneCounter.get(originalPhone) || 0;
      phoneCounter.set(originalPhone, count + 1);

      if (count === 0) {
        return {
          ...user,
          phoneNumber: originalPhone,
        };
      }

      const suffix = String(count).padStart(2, "0");

      return {
        ...user,
        phoneNumber: `${originalPhone}-${suffix}`,
      };
    });
  };

  const handleExcelFileUpload = (file: File) => {
    if (!selectedExcelFormat) {
      setExpiryDateError("Please select an Excel format first");
      return;
    }

    setIsSelectorDisabled(true);
    setExpiryIssuesNotice(null);
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: false, // This will convert dates to strings
          dateNF: "dd/mm/yyyy", // Format for date conversion
        });

        console.log("Excel parsed data:", jsonData);

        // Convert to array format based on selected format
        let result;
        switch (selectedExcelFormat) {
          case "NETWALA":
            result = handleNetwalaExcelDataImportAndFormulateTheData(jsonData);
            break;
          case "JAZZ_ZONG":
            result = handleJazzZongExcelDataImportAndFormulateTheData(jsonData);
            break;
          case "REDTONE":
            result = handleRedToneExcelDataImportAndFormulateTheData(jsonData);
            break;
          default:
            result = {
              packages: [],
              users: [],
              usersWithNoPackage: [],
              error: "Unknown Excel format",
            };
        }

        console.log("Formatted result:", result);
        const { users, packages, usersWithNoPackage, error } = result;
        // ✅ DUPLICATE SUMMARY
        const duplicateInfo = getDuplicateSummary(users);
        setDuplicateSummary(duplicateInfo);

        if (error !== null) {
          setExpiryDateError(error);
          setIsSelectorDisabled(false);
          return;
        }

        // Check for duplicate phone numbers
        // const duplicateNumbers = checkForDuplicatePhoneNumbers(users);
        // if (duplicateNumbers.length > 0) {
        //   const errorMessage = `You have duplicate phone numbers: ${duplicateNumbers.join(
        //     ", "
        //   )}. Please remove duplicates before uploading.`;
        //   setExpiryDateError(errorMessage);
        //   setIsSelectorDisabled(false);
        //   return;
        // }
        //now add this variable
        console.log(
          "Before Normalize:",
          users.map((u) => u.phoneNumber),
        );

        const normalizedUsers = normalizeDuplicatePhoneNumbers(users);

        console.log(
          "After Normalize:",
          normalizedUsers.map((u) => u.phoneNumber),
        );

        const expiryIssuesCount = (usersWithNoPackage || []).filter(
          (u) => Array.isArray((u as any).importErrors) && (u as any).importErrors.length > 0,
        ).length;
        if (expiryIssuesCount > 0) {
          setExpiryIssuesNotice(
            `Expiry is missing/invalid for ${expiryIssuesCount} users. They have been moved to the Missed Users tab so you can skip/fix them before upload.`,
          );
        }

        setIsDataLoaded(true);
        setUsers(normalizedUsers);
        setIspPackages(packages);
        setUsersWithNoPackage(usersWithNoPackage);
      } catch (error) {
        console.error("Error reading Excel file:", error);
        setExpiryDateError(
          "Error reading Excel file. Please ensure the file format is correct.",
        );
        setIsSelectorDisabled(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };
  // const handleUploadRawData = (data: any[], _selectedFormat: string) => {
  //   setIsSelectorDisabled(true);
  //   switch (selectedFormat) {
  //     case "TRANSWORLD":
  //       {
  //         const result = handleTransworldDataImportAndFormulateTheData(data);
  //         const { users, packages, usersWithNoPackage, error } = result;
  //         if (error !== null) {
  //           setExpiryDateError(error);
  //           setIsSelectorDisabled(false);
  //           return;
  //         }
  //         // Check for duplicate phone numbers
  //         // const duplicateNumbers = checkForDuplicatePhoneNumbers(users);
  //         // if (duplicateNumbers.length > 0) {
  //         //   const errorMessage = `You have duplicate phone numbers: ${duplicateNumbers.join(
  //         //     ", "
  //         //   )}. Please remove duplicates before uploading.`;
  //         //   setExpiryDateError(errorMessage);
  //         //   setIsSelectorDisabled(false);
  //         //   return;
  //         // }
  //         const duplicateInfo = getDuplicateSummary(users);
  //         setDuplicateSummary(duplicateInfo);
  //         const normalizedUsers = normalizeDuplicatePhoneNumbers(users);
  //         console.log(
  //           "Before Normalize:",
  //           users.map((u) => u.phoneNumber),
  //         );
  //         console.log(
  //           "After Normalize:",
  //           normalizedUsers.map((u) => u.phoneNumber),
  //         );

  //         setIsDataLoaded(true);
  //         setUsers(normalizedUsers);
  //         setIspPackages(packages);
  //         setUsersWithNoPackage(usersWithNoPackage);
  //       }
  //       break;
  //     case "ZALPRO":
  //       {
  //         const result = handleZalProDataImportAndFormulateTheData(data);
  //         const { users, packages, usersWithNoPackage, error } = result;
  //         if (error !== null) {
  //           setExpiryDateError(error);
  //           setIsSelectorDisabled(false);
  //           return;
  //         }
  //         // Check for duplicate phone numbers
  //         // const duplicateNumbers = checkForDuplicatePhoneNumbers(users);
  //         // if (duplicateNumbers.length > 0) {
  //         //   const errorMessage = `You have duplicate phone numbers: ${duplicateNumbers.join(
  //         //     ", "
  //         //   )}. Please remove duplicates before uploading.`;
  //         //   setExpiryDateError(errorMessage);
  //         //   setIsSelectorDisabled(false);
  //         //   return;
  //         // }
  //         const normalizedUsers = normalizeDuplicatePhoneNumbers(users);

  //         setIsDataLoaded(true);
  //         setUsers(normalizedUsers);
  //         setIspPackages(packages);
  //         setUsersWithNoPackage(usersWithNoPackage);
  //       }
  //       break;
  //     case "NBB":
  //       {
  //         const result = handleNBBDataImportAndFormulateTheData(data);

  //         const { users, packages, usersWithNoPackage, error } = result;
  //         if (error !== null) {
  //           setExpiryDateError(error);
  //           setIsSelectorDisabled(false);
  //           return;
  //         }
  //         // Check for duplicate phone numbers
  //         // const duplicateNumbers = checkForDuplicatePhoneNumbers(users);
  //         // if (duplicateNumbers.length > 0) {
  //         //   const errorMessage = `You have duplicate phone numbers: ${duplicateNumbers.join(
  //         //     ", "
  //         //   )}. Please remove duplicates before uploading.`;
  //         //   setExpiryDateError(errorMessage);
  //         //   setIsSelectorDisabled(false);
  //         //   return;
  //         // }
  //         const normalizedUsers = normalizeDuplicatePhoneNumbers(users);

  //         setIsDataLoaded(true);
  //         setUsers(normalizedUsers);
  //         setIspPackages(packages);
  //         setUsersWithNoPackage(usersWithNoPackage);
  //       }
  //       break;
  //   }
  // };

  // now add
  const handleUploadRawData = (data: any[], _selectedFormat: string) => {
    setIsSelectorDisabled(true);
    setExpiryIssuesNotice(null);

    let result;

    switch (selectedFormat) {
      case "TRANSWORLD":
        result = handleTransworldDataImportAndFormulateTheData(data);
        break;
      case "ZALPRO":
        result = handleZalProDataImportAndFormulateTheData(data);
        break;
      case "NBB":
        result = handleNBBDataImportAndFormulateTheData(data);
        break;
      default:
        return;
    }

    const { users, packages, usersWithNoPackage, error } = result;

    if (error !== null) {
      setExpiryDateError(error);
      setIsSelectorDisabled(false);
      return;
    }

    // ✅ DUPLICATE SUMMARY (for UI)
    const duplicateInfo = getDuplicateSummary(users);
    setDuplicateSummary(duplicateInfo);

    // ✅ NORMALIZE DUPLICATES
    const normalizedUsers = normalizeDuplicatePhoneNumbers(users);

    const expiryIssuesCount = (usersWithNoPackage || []).filter(
      (u) => Array.isArray((u as any).importErrors) && (u as any).importErrors.length > 0,
    ).length;
    if (expiryIssuesCount > 0) {
      setExpiryIssuesNotice(
        `Expiry is missing/invalid for ${expiryIssuesCount} users. They have been moved to the Missed Users tab so you can skip/fix them before upload.`,
      );
    }

    setIsDataLoaded(true);
    setUsers(normalizedUsers);
    setIspPackages(packages);
    setUsersWithNoPackage(usersWithNoPackage);
  };

  const handleUserSkip = (id: string) => {
    console.log({ id });
    const userToSkip = users.find((item) => item.id === id);
    if (userToSkip) {
      // Move user to missed users instead of just removing
      setUsersWithNoPackage((prev) => [...prev, userToSkip]);
    }
    const updatedUsers = users.filter((item) => item.id !== id);
    console.log({ updatedUsers });
    setUsers(updatedUsers);
  };
  const handlePackageMapper = (mappedData: MappedPackagesProp[]) => {
    console.log(mappedData);
    setMappedPackages(mappedData);
    const updatedUsers = users.map((user) => {
      const matchingPackage = mappedData.find(
        (pkg) => pkg.packageName === user.packageName,
      );
      if (matchingPackage) {
        user.intPkPackageID = matchingPackage.packageId;
        user.mappedPackageName = matchingPackage.mappedPackageName;
      }
      return user;
    });
    setUsers(updatedUsers);
    setIsPackagesMapped(true);
    setActiveStep(2);
  };
  const chunkData = (_users: BulkUserImportData[]) => {
    const chunks = [];
    for (let i = 0; i < _users.length; i += chunkSize) {
      chunks.push(_users.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const handleBulkImportApi = () => {
    // Check for duplicate phone numbers before uploading
    // const duplicateNumbers = checkForDuplicatePhoneNumbers(users);

    // if (duplicateNumbers.length > 0) {
    //   const errorMessage = `You have duplicate phone numbers: ${duplicateNumbers.join(
    //     ", "
    //   )}. Please remove duplicates before uploading.`;
    //   setExpiryDateError(errorMessage);
    //   openNotification("error", "Duplicate Numbers Found", errorMessage);
    //   return;
    // }
    // console.log("Final Users Before Upload:",
    //   users.map(u => u.phoneNumber)
    // );

    console.log({ usersBeforeUpload: users });
    socket.emit("uploadBulkUsersData", {
      users: users,
      intPkIspID: Number(intPkIspID),
      authToken: authTokenString,
    });
    setIsLoading(true);
    return;

    // const chunks = chunkData(users);
    // setChunks(chunks);

    // setTotalChunks(chunks.length);
    // setIsLoading(true);
    // createNewBulkRequestApi({
    //   intPkIspID,
    //   totalChunks: chunks.length,
    // });
  };
  useEffect(() => {
    if (bulkRequestApiError !== null && bulkRequestApiErrorCount !== 0) {
      openNotification(`error`, "Operation Failed", bulkRequestApiError);
    }
  }, [bulkRequestApiError, bulkRequestApiErrorCount]);

  useEffect(() => {
    if (
      usersImportRequestApiError !== null &&
      usersImportRequestApiErrorCount !== 0
    ) {
      openNotification(`error`, "Operation Failed", usersImportRequestApiError);
    }
  }, [usersImportRequestApiError, usersImportRequestApiErrorCount]);

  useEffect(() => {
    if (bulkRequestApiResponse !== null) {
      const data: any = bulkRequestApiResponse.responseData;
      const id = data.id;
      console.log({ id });

      if (id) {
        //lets send the data
        for (let i = 0; i < chunks.length; i++) {
          bulkImportUsersByIspApi({
            intPkIspID,
            requestId: id,
            chunkNumber: i + 1,
            users: chunks[i],
          });
        }
      }
    }
  }, [bulkRequestApiResponse]);

  useEffect(() => {
    if (usersImportRequestApiResponse !== null) {
      const data: any = usersImportRequestApiResponse.responseData;
      const { message } = usersImportRequestApiResponse;
      if (data.isRequestCompleted === true && message) {
        console.log({ data, message });
        openNotification(`success`, "Success", message);
        navigate("/");
      }
    }
  }, [usersImportRequestApiResponse]);
  const handleClose = (isSelected: boolean) => {
    setShow(false);
    if (isSelected === true) {
      console.log({ users });
      handleBulkImportApi();
    }
  };
  const handlePackageCreateClose = (isReload: boolean) => {
    if (isReload) {
      postApiData({ ispId: intPkIspID });
    }
    setShowCreatePackage(false);
  };
  useEffect(() => {
    socket.on(`errorUploadingUsers`, (data) => {
      console.log({ data });
      openNotification(
        `error`,
        "Operation Failed",
        data.message || "Error While Uploading",
      );
      setIsLoading(false);
    });
    socket.on(`bulkUserUploaded`, (data) => {
      console.log({ data });
      openNotification(
        `success`,
        "Success",
        data.message || "Users Uploaded Successfully",
      );
      setTimeout(() => {
        navigate("/");
      }, 2500);
    });
  }, []);

  return (
    <>
      <Row
        gutter={[32, 32]}
        justify="end"
        style={{ width: "100%", marginBottom: "20px", marginTop: "20px" }}
      >
     
    <div style={{ textAlign: "right" }}>
          <NetwalaButton
            backgroundColor={ButtonProps.background.STANDARD}
            height={ButtonProps.height.STANDARD}
            width={ButtonProps.width.STANDARD}
            textColor={ButtonProps.color.STANDARD}
            icon={<img src={PlusIcon} />}
            onClick={() => {
              setShowCreatePackage(true);
            }}
            text="Create Package"
          />
          </div>
      </Row>
      {show && (
        <ImportUserConfirmationModal show={show} handleClose={handleClose} />
      )}

      {showCreatePackage && (
        <CreatePackageOnTheGoModalComponent
          show={showCreatePackage}
          handleClose={handlePackageCreateClose}
          intPkIspID={intPkIspID}
        />
      )}
      {contextHolder}
      <Row gutter={[16, 16]} style={{ width: "100%", marginTop: "20px" }}>
        <Col offset={6} span={12}>
          <label className="bulk-user-import-label">Choose File Type</label>
          <Radio.Group
            value={fileType}
            onChange={(e) => {
              setFileType(e.target.value);
              // Reset states when switching file types
              setSelectedFormat("");
              setIsDataLoaded(false);
              setIsSelectorDisabled(false);
              setUsers([]);
              setIspPackages([]);
              setUsersWithNoPackage([]);
              setExpiryDateError(null);
              setExpiryIssuesNotice(null);
              setActiveStep(1);
              setIsPackagesMapped(false);
              setActiveTab("users");
              setDuplicateSummary([]);
            }}
            style={{ width: "100%", marginTop: "10px" }}
          >
            <Radio value="CSV">CSV</Radio>
            <Radio value="EXCEL">Excel</Radio>
          </Radio.Group>
        </Col>

        {fileType === "CSV" && (
          <>
            <Col offset={6} span={12}>
              <label className="bulk-user-import-label">
                Choose ISP Format
              </label>
              <NetwalaSelect
                value={selectedFormat}
                onChange={handleSelectedFormat}
                size="large"
                height="45px"
                options={importFormatIspTypes}
                disabled={isSelectorDisabled}
              />
            </Col>
            {selectedFormat && (
              <Col offset={6} span={12}>
                <CSVReader
                  onUploadAccepted={(results: any) => {
                    console.log("---------------------------");
                    console.log(results);
                    handleUploadRawData(results.data, selectedFormat);
                    console.log("---------------------------");
                    setZoneHover(false);
                  }}
                  onDragOver={(event: DragEvent) => {
                    event.preventDefault();
                    setZoneHover(true);
                  }}
                  onDragLeave={(event: DragEvent) => {
                    event.preventDefault();
                    setZoneHover(false);
                  }}
                >
                  {({
                    getRootProps,
                    acceptedFile,
                    ProgressBar,
                    getRemoveFileProps,
                    Remove,
                  }: any) => (
                    <>
                      <div
                        {...getRootProps()}
                        style={Object.assign(
                          {},
                         { ...styles.zone,
                          ...(zoneHover && styles.zoneHover),
                             marginTop: "10px",
                    marginBottom:"10px",
                    height:"260px"}
                        )}
                      >
                        {acceptedFile ? (
                          <>
                            <div style={styles.file}>
                              <div style={styles.info}>
                                <span style={styles.size}>
                                  {formatFileSize(acceptedFile.size)}
                                </span>
                                <span style={styles.name}>
                                  {acceptedFile.name}
                                </span>
                              </div>
                              <div style={styles.progressBar}>
                                <ProgressBar />
                              </div>
                              <div
                                {...getRemoveFileProps()}
                                style={styles.remove}
                                onMouseOver={(event: Event) => {
                                  event.preventDefault();
                                  setRemoveHoverColor(REMOVE_HOVER_COLOR_LIGHT);
                                }}
                                onMouseOut={(event: Event) => {
                                  event.preventDefault();
                                  setRemoveHoverColor(
                                    DEFAULT_REMOVE_HOVER_COLOR,
                                  );
                                }}
                              >
                                <Remove color={removeHoverColor} />
                              </div>
                            </div>
                          </>
                        ) : (
                          "Drop CSV file here or click to upload"
                        )}
                      </div>
                    </>
                  )}
                </CSVReader>
              </Col>
            )}
          </>
        )}
        {fileType === "EXCEL" && (
          <>
            <Col offset={6} span={12}>
              <label className="bulk-user-import-label">
                Choose Excel Format
              </label>
              <NetwalaSelect
                value={selectedExcelFormat}
                onChange={(value) => {
                  setSelectedExcelFormat(value);
                  setDuplicateSummary([]);
                  setExpiryIssuesNotice(null);

                  // Reset states when changing format
                  setIsDataLoaded(false);
                  setIsSelectorDisabled(false);
                  setUsers([]);
                  setIspPackages([]);
                  setUsersWithNoPackage([]);
                  setExpiryDateError(null);
                  setActiveStep(1);
                  setIsPackagesMapped(false);
                  setActiveTab("users");
                }}
                size="large"
                height="45px"
                options={excelFormatTypes}
                disabled={isSelectorDisabled}
              />
            </Col>
            {selectedExcelFormat && (
              <Col offset={6} span={12}>
                <label className="bulk-user-import-label">
                  Upload Excel File
                </label>
                {selectedExcelFormat === "NETWALA" && (
                  <div style={{ marginTop: "10px", marginBottom: "10px",height:"50px" }}>
                    <NetwalaButton
                      backgroundColor={ButtonProps.background.STANDARD}
                      height={ButtonProps.height.STANDARD}
                      width={ButtonProps.width.STANDARD}
                      textColor={ButtonProps.color.STANDARD}
                      icon={<DownloadOutlined />}
                      onClick={() => {
                        // Create a link to download the sample file
                        const link = document.createElement("a");
                        link.href = "/netwala-import-format-sample.xlsx";
                        link.download = "netwala-import-format-sample.xlsx";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      text="Download Sample File"
                    />
                  </div>
                )}
                <div
                  style={{
                    ...styles.zone,
                    ...(zoneHover && styles.zoneHover),
                    marginTop: "10px",
                    marginBottom:"10px",
                    height:"260px"
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setZoneHover(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setZoneHover(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setZoneHover(false);
                    const files = e.dataTransfer.files;
                    if (files.length > 0) {
                      const file = files[0];
                      if (
                        file.name.endsWith(".xlsx") ||
                        file.name.endsWith(".xls")
                      ) {
                        handleExcelFileUpload(file);
                      } else {
                        setExpiryDateError(
                          "Please upload a valid Excel file (.xlsx or .xls)",
                        );
                      }
                    }
                  }}
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".xlsx,.xls";
                    input.onchange = (e: any) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleExcelFileUpload(file);
                      }
                    };
                    input.click();
                  }}
                >
                  {isSelectorDisabled ? (
                    <div style={styles.file}>
                      <div style={styles.info}>
                        <span style={styles.name}>File uploaded</span>
                      </div>
                    </div>
                  ) : (
                    "Drop Excel file here or click to upload"
                  )}
                </div>
              </Col>
            )}
          </>
        )}

        {/* error  */}
        {expiryDateError !== null && (
          <Col offset={6} span={12}>
            <Alert
              message="Error"
              description={expiryDateError}
              type="error"
              showIcon
            />
          </Col>
        )}

        {expiryIssuesNotice !== null && (
          <Col offset={6} span={12}>
            <Alert
              message="Expiry validation"
              description={expiryIssuesNotice}
              type="warning"
              showIcon
            />
          </Col>
        )}

        {duplicateSummary.length > 0 && (
          <Col
            offset={6}
            span={12}
            style={{  marginBottom: "10px" }}
          >
            <Alert
              message="Duplicate Numbers Found"
              type="warning"
              showIcon
              description={
                <div>
                  {duplicateSummary.map((item) => (
                    <div key={item.number}>
                      {item.number} ({item.count} times)
                    </div>
                  ))}
                </div>
              }
            />
          </Col>
        )}

        {activeStep > 1 && (
          <Col span={24}>
            <NetwalaButton
              backgroundColor={ButtonProps.background.STANDARD}
              height={ButtonProps.height.STANDARD}
              width={ButtonProps.width.STANDARD}
              textColor={ButtonProps.color.STANDARD}
              icon={<img src={BackButtonLong} />}
              onClick={() => {
                setActiveStep((activeStep) => activeStep - 1);
              }}
              text="Back"
            />
            
          </Col>
        )}
        {((fileType === "CSV" && selectedFormat && isDataLoaded) ||
          (fileType === "EXCEL" && selectedExcelFormat && isDataLoaded)) && (
          <>
            {activeStep === 1 && (
              <Col span={24} >
                <PackageMapperTable
                  packages={packages}
                  ispPackages={ispPackages}
                  handlePackageMapper={handlePackageMapper}
                  alreadyMappedPackages={mappedPackages}
                />
              </Col>
            )}
            {isPackagesMapped && activeStep === 2 && (
              <Col span={24}>
                {isLoading === true ? (
                  <TailSpinLoader />
                ) : (
                  <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                      {
                        key: "users",
                        label: `Users (${users.length})`,
                        children: (
                          <BulkUsersImportTable
                            users={users}
                            handleUserSkip={handleUserSkip}
                            showSkipButton={true}
                          />
                        ),
                      },
                      {
                        key: "missed",
                        label: `Missed Users (${usersWithNoPackage.length})`,
                        children: (
                          <BulkUsersImportTable
                            users={usersWithNoPackage}
                            handleUserSkip={() => {}}
                            showSkipButton={false}
                          />
                        ),
                      },
                    ]}
                  />
                )}
                {activeTab === "users" && (
                  <div
                    style={{
                      width: "100%",
                      marginTop: "20px",
                      marginBottom: "20px",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <NetwalaButton
                      backgroundColor={ButtonProps.background.STANDARD}
                      height={ButtonProps.height.STANDARD}
                      width={ButtonProps.width.STANDARD}
                      textColor={ButtonProps.color.STANDARD}
                      onClick={() => {
                        setShow(true);
                      }}
                      disabled={isLoading}
                      text="Upload Users"
                    />
                  </div>
                )}
              </Col>
            )}
          </>
        )}
      </Row>
    </>
  );
};
export default ISPBulkUserImport;
